import React from 'react';
import {Avatar, Chip, Divider, Link as MuiLink, Paper, Stack, Tooltip, Typography} from '@mui/material';
import {formatTimestamp} from '../../../utils/formatTimestamp';
import {resolveReportHeaderAuthors} from '../../../utils/resolveReportHeaderAuthors';
import {buildReportExcerptUrl} from '../../collection-requests/coverage/navigateToReportExcerpt';
import NodeRenderer from '../../products/previewPage/NodeRenderer';
import TabLoading from './TabLoading';
import TabEmpty from './TabEmpty';
import {usePagination} from './usePagination';
import TabPagination from './TabPagination';


/**
 * Claims tab — displays entity mentions in FINTEL or Draft reports.
 *
 * @param {Object} props
 * @param {Array} props.claims
 * @param {boolean} props.loading
 * @param {string} props.emptyMessage
 */
function toNodeDoc(blockContent) {
    if (!blockContent) return null;

    const parsed = typeof blockContent === 'string'
        ? (() => {
            try {
                return JSON.parse(blockContent);
            } catch {
                return null;
            }
        })()
        : blockContent;

    if (!parsed) return null;
    if (parsed.type === 'doc' && Array.isArray(parsed.content)) return parsed;
    if (Array.isArray(parsed)) return {type: 'doc', content: parsed};
    if (parsed.type) return {type: 'doc', content: [parsed]};

    return null;
}

/** Plain text from a ProseMirror node (or string), for the same ?q= convention as citation chips. */
function excerptTextFromNode(node) {
    if (!node) return '';
    if (typeof node === 'string') return node;
    if (typeof node.text === 'string') return node.text;
    if (Array.isArray(node.content)) {
        return node.content.map(excerptTextFromNode).join('');
    }
    return '';
}

function resolveClaimExcerpt(claim, blockDoc) {
    const fromBlock = excerptTextFromNode(blockDoc).replace(/\s+/g, ' ').trim();
    if (fromBlock) return fromBlock;
    const equivalent = claim.excerpt || claim.claim || claim.text || claim.claim_text;
    return typeof equivalent === 'string' ? equivalent.trim() : '';
}

function reportProductUrl(reportKey, excerpt) {
    if (!reportKey || reportKey === 'unknown-report') return null;
    return buildReportExcerptUrl(reportKey, excerpt);
}

function getReportLabel(claim, reportHeadersById) {
    const reportKey = claim.reportId || claim.report_id || 'unknown-report';
    const header = reportHeadersById?.[reportKey];
    return header?.title || claim.report_title || claim.reportTitle || reportKey || 'Unknown report';
}


function toMetaLabel(option) {
    if (!option) return null;
    if (typeof option === 'string') return option;
    return option.label || option.value || null;
}

function formatCreatedAt(value) {
    return formatTimestamp(value, null);
}

function ClaimPassage({claim, reportKey, currentEntityWikidataId}) {
    if (claim.blockDoc?.content?.length) {
        return claim.blockDoc.content.map((node, idx) => (
            <NodeRenderer
                key={`${claim.id || reportKey}-${claim.originalIndex}-node-${idx}`}
                node={node}
                idx={idx}
                currentEntityWikidataId={currentEntityWikidataId}
            />
        ));
    }
    return <Typography variant="body2" component="span" display="block">{claim.claimText}</Typography>;
}

const ClaimsTab = ({
                       claims,
                       reportHeadersById = {},
                       loading,
                       emptyMessage,
                       onAuthorClick,
                       currentEntityWikidataId,
                   }) => {
    const groupedClaims = React.useMemo(() => {
        const reports = new Map();

        claims.forEach((claim, originalIndex) => {
            const reportKey = claim.reportId || claim.report_id || 'unknown-report';
            const reportHeader = reportHeadersById?.[reportKey] || null;
            const headerMeta = reportHeader?.meta || {};
            const reportLabel = getReportLabel(claim, reportHeadersById);
            const reportAuthors = resolveReportHeaderAuthors(reportHeader);
            const reportClassification = toMetaLabel(headerMeta.classification || claim.classification);
            const reportOriginator = toMetaLabel(headerMeta.originator || claim.originator);
            const reportIcrNumber = headerMeta.icrNumber || claim.icrNumber || null;
            const headerStatus = reportHeadersById?.[reportKey]?.status;
            const status = claim.report_status || claim.status || headerStatus || '—';
            const claimText = claim.claim || claim.text || claim.claim_text
                || claim.wikidataLabel || claim.wikidata_label
                || claim.wikidataId || claim.wikidata_id
                || '—';
            const blockDoc = toNodeDoc(claim.blockContent || claim.block_content);
            const excerpt = resolveClaimExcerpt(claim, blockDoc);
            const claimLink = reportProductUrl(reportKey, excerpt);
            const rawPosition = claim.position;
            const parsedPosition = rawPosition != null ? Number(rawPosition) : NaN;
            const position = Number.isFinite(parsedPosition) ? parsedPosition : null;
            const reportCreatedAt =
                reportHeader?.createdAt ||
                reportHeader?.createAt ||
                reportHeader?.created_at ||
                claim.reportCreatedAt ||
                claim.report_createAt ||
                claim.report_created_at ||
                null;
            const reportCreatedAtLabel = formatCreatedAt(reportCreatedAt);

            if (!reports.has(reportKey)) {
                reports.set(reportKey, {
                    key: reportKey,
                    label: reportLabel,
                    link: reportProductUrl(reportKey, excerpt),
                    authors: reportAuthors,
                    classification: reportClassification,
                    originator: reportOriginator,
                    icrNumber: reportIcrNumber,
                    status,
                    createdAtLabel: reportCreatedAtLabel,
                    claims: []
                });
            }

            const reportEntry = reports.get(reportKey);
            if (!reportEntry.createdAtLabel && reportCreatedAtLabel) {
                reportEntry.createdAtLabel = reportCreatedAtLabel;
            }

            reportEntry.claims.push({
                ...claim,
                claimText,
                blockDoc,
                excerpt,
                link: claimLink,
                position,
                originalIndex,
            });
        });

        return Array.from(reports.values()).map((report) => {
            const claims = [...report.claims].sort((a, b) => {
                if (a.position === null && b.position === null) return a.originalIndex - b.originalIndex;
                if (a.position === null) return 1;
                if (b.position === null) return -1;
                if (a.position !== b.position) return a.position - b.position;
                return a.originalIndex - b.originalIndex;
            });
            const excerptForReport = claims.find((c) => c.excerpt)?.excerpt || '';
            return {
                ...report,
                link: reportProductUrl(report.key, excerptForReport),
                claims,
            };
        });
    }, [claims, reportHeadersById]);

    // Flatten all sorted claims from every report group into a single ordered list
    // so that pagination applies to individual claims (20 per page), not report groups.
    const flatClaims = React.useMemo(
        () => groupedClaims.flatMap((report) =>
            report.claims.map((claim) => ({...claim, _report: report}))
        ),
        [groupedClaims],
    );

    const {page, setPage, pageItems: pageClaims, totalPages, hasPagination} = usePagination(flatClaims);

    // For the visible page window of claims, emit a report-header row whenever the
    // report changes relative to the previous claim on this page.
    const pageGroups = React.useMemo(() => {
        const groups = [];
        let lastReportKey = null;
        for (const claim of pageClaims) {
            const r = claim._report;
            if (r.key !== lastReportKey) {
                groups.push({report: r, claims: []});
                lastReportKey = r.key;
            }
            groups[groups.length - 1].claims.push(claim);
        }
        return groups;
    }, [pageClaims]);

    if (loading) return <TabLoading/>;
    if (!claims.length) return <TabEmpty message={emptyMessage}/>;

    return (
        <Stack>
        <Stack spacing={2} sx={{bgcolor: 'grey.400'}}>
            {pageGroups.map(({report, claims: groupClaims}) => (
                <Paper key={report.key} variant="outlined" sx={{borderRadius: 0, border: 'none'}}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} p={2}>
                        <Stack spacing={2}>
                            {report.link ? (
                                <MuiLink
                                    href={report.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    variant="subtitle2"
                                    sx={{fontWeight: 600}}
                                    data-testid="report-excerpt-link"
                                >
                                    {report.label}
                                </MuiLink>
                            ) : (
                                <Typography variant="subtitle2">{report.label}</Typography>
                            )}
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                {!!report.classification && (
                                    <Chip size="small" variant="outlined" label={`Class: ${report.classification}`}/>
                                )}
                                {!!report.originator && (
                                    <Chip size="small" variant="outlined" label={`Originator: ${report.originator}`}/>
                                )}
                                {!!report.icrNumber && (
                                    <Chip size="small" variant="outlined" label={`ICR: ${report.icrNumber}`}/>
                                )}
                            </Stack>
                            {!!report.createdAtLabel && (
                                <Typography variant="caption" color="text.secondary">
                                    Created: {report.createdAtLabel}
                                </Typography>
                            )}
                            {!!report.authors.length && (
                                <Stack spacing={1}>
                                    <Typography variant={'body2'} color={'text.secondary'}>Authors:</Typography>
                                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                        {report.authors.map((author, idx) => {
                                            const authorKey = author.uid || `${report.key}-author-${idx}`;
                                            const initials = author.displayName
                                                .split(' ')
                                                .filter(Boolean)
                                                .map((w) => w[0].toUpperCase())
                                                .slice(0, 2)
                                                .join('');
                                            return (
                                                <Chip
                                                    key={authorKey}
                                                    label={author.displayName}
                                                    variant="outlined"
                                                    clickable
                                                    avatar={
                                                        <Avatar
                                                            src={author.profilePicture || undefined}
                                                            alt={author.displayName}
                                                            sx={{width: 24, height: 24, fontSize: 10}}
                                                        >
                                                            {!author.profilePicture && initials}
                                                        </Avatar>
                                                    }
                                                    onClick={(event) => {
                                                        onAuthorClick?.({
                                                            id: author.uid || undefined,
                                                            name: author.displayName,
                                                            label: author.displayName,
                                                            email: author.email || undefined,
                                                            position: author.position || undefined,
                                                            headshot: author.headshot || undefined,
                                                            anchorEl: event.currentTarget,
                                                        });
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Chip label={report.status} size="small" variant="outlined"/>
                            <Chip
                                label={`${groupClaims.length} mention${groupClaims.length === 1 ? '' : 's'}`}
                                size="small"
                                variant="outlined"
                            />
                        </Stack>
                    </Stack>
                    <Divider sx={{my: 1}}/>
                    <Stack pb={1}>
                        {groupClaims.map((claim) => (
                            <Stack
                                key={claim.id || `${report.key}-${claim.originalIndex}`}
                                direction="row"
                                alignItems="flex-start"
                                px={2}
                                py={1}
                            >
                                <Tooltip title={'Block position in report'} placement="top">
                                    <Typography variant="caption" sx={{minWidth: 28, color: 'text.secondary', pt: 0.5}}>
                                        {claim.position !== null ? `${claim.position}.` : '—'}
                                    </Typography>
                                </Tooltip>
                                <Stack flex={1} spacing={0.5}>
                                    {claim.link ? (
                                        <MuiLink
                                            href={claim.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            color="inherit"
                                            data-testid="claim-excerpt-link"
                                        >
                                            <ClaimPassage
                                                claim={claim}
                                                reportKey={report.key}
                                                currentEntityWikidataId={currentEntityWikidataId}
                                            />
                                        </MuiLink>
                                    ) : (
                                        <ClaimPassage
                                            claim={claim}
                                            reportKey={report.key}
                                            currentEntityWikidataId={currentEntityWikidataId}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                </Paper>
            ))}
        </Stack>
        <TabPagination
            hasPagination={hasPagination}
            page={page}
            totalPages={totalPages}
            totalItems={flatClaims.length}
            onChange={setPage}
        />
        </Stack>
    );
};

export default ClaimsTab;

